import {
  Bell,
  Binoculars,
  BookmarkSimple,
  Cards,
  Clock,
  ClosedCaptioning,
  Crop,
  Eraser,
  Export,
  FileText,
  FilmSlate,
  FilmStrip,
  Fingerprint,
  Fire,
  Flask,
  Folder,
  Funnel,
  GitBranch,
  GitMerge,
  GridNine,
  Hash,
  Hourglass,
  Image,
  Link,
  Microphone,
  MusicNotes,
  Palette,
  PaperPlaneTilt,
  PencilSimple,
  Scan,
  Scissors,
  SealCheck,
  ShieldWarning,
  SlidersHorizontal,
  Snowflake,
  Sparkle,
  TextT,
  Timer,
  Translate,
  TrendUp,
  Trophy,
  UserCheck,
  WebhooksLogo,
} from '@phosphor-icons/react';
import {
  type Category,
  DATA_TYPE_LABELS,
  type DataType,
  defaultConfig,
  type EdgeType,
  IMAGE_MODELS,
  type InputPort,
  type NodeDefinition,
  VIDEO_MODELS,
} from '@reelplicate/flows';
import type { FlowDataType } from '@reelplicate/ui';
import type { ElementType } from 'react';

type Icon = ElementType<{ className?: string }>;

/** The catalogue names icons as strings (it has no React); this resolves them. */
export const NODE_ICONS: Record<string, Icon> = {
  Bell,
  Binoculars,
  BookmarkSimple,
  Cards,
  Clock,
  ClosedCaptioning,
  Crop,
  Eraser,
  Export,
  FileText,
  Fingerprint,
  Fire,
  Flask,
  Folder,
  FilmSlate,
  FilmStrip,
  Funnel,
  GitBranch,
  GitMerge,
  GridNine,
  Hash,
  Hourglass,
  Image,
  Link,
  Microphone,
  MusicNotes,
  Palette,
  PaperPlaneTilt,
  PencilSimple,
  Scan,
  Scissors,
  SealCheck,
  ShieldWarning,
  SlidersHorizontal,
  Snowflake,
  Sparkle,
  TextT,
  Timer,
  Translate,
  TrendUp,
  Trophy,
  UserCheck,
  WebhooksLogo,
};

export function nodeIcon(definition: Pick<NodeDefinition, 'icon'> | undefined): Icon {
  return (definition && NODE_ICONS[definition.icon]) ?? Sparkle;
}

/** One node's category, as the canvas subtitle says it ("Generate"). */
export const CATEGORY_NAME: Record<Category, string> = {
  starter: 'Starter',
  filter: 'Filter',
  generate: 'Generate',
  edit: 'Edit',
  experiment: 'Experiment',
  guardrail: 'Guardrail',
  output: 'Output',
};

const PLURALS: Record<DataType, string> = {
  source: 'sources',
  blueprint: 'blueprints',
  script: 'scripts',
  clip: 'clips',
  reel: 'reels',
  carousel: 'carousels',
  image: 'images',
};

/** "15 scripts", "1 reel". */
export function countLabel(count: number, type: DataType): string {
  return `${count} ${count === 1 ? DATA_TYPE_LABELS[type].toLowerCase() : PLURALS[type]}`;
}

/** The chip colour for an edge: its data type, or orange for a variant set. */
export function chipType(type: EdgeType): FlowDataType {
  return type.variant ? 'variant' : type.base;
}

function acceptsLabel(port: InputPort): string {
  if (port.accepts === 'trigger') return 'Trigger';
  if (port.accepts === 'any') return port.variant === 'required' ? 'Variant set' : 'Any';
  return port.accepts.map((type) => DATA_TYPE_LABELS[type]).join(' · ');
}

/**
 * The library's IO line, e.g. "Script → Script ×N", "Reel → Clip ×2",
 * "— → Source". ×N marks nodes that multiply what comes in.
 */
export function ioLabel(definition: NodeDefinition): string {
  const inputs = definition.inputs.filter((port) => port.accepts !== 'trigger');
  const from = inputs.length ? [...new Set(inputs.map(acceptsLabel))].join(' + ') : '—';
  const outputs = definition.outputs.filter((port) => port.type !== 'trigger');
  if (definition.outputs.some((port) => port.type === 'trigger'))
    return `${from} → Trigger`;
  if (outputs.length === 0) return `${from} → —`;

  const names = [
    ...new Set(
      outputs.map((port) =>
        port.variant === 'set'
          ? 'Variant set'
          : port.type === 'same'
            ? 'same'
            : DATA_TYPE_LABELS[port.type as DataType],
      ),
    ),
  ];
  let to = names.join(' · ');
  if (outputs.length > 1) to += ` ×${outputs.length}`;
  else if (fansOut(definition)) to += ' ×N';
  return `${from} → ${to}`;
}

function fansOut(definition: NodeDefinition): boolean {
  const inputs = Object.fromEntries(
    definition.inputs
      .filter((port) => port.accepts !== 'trigger')
      .map((port) => [port.id, 1]),
  );
  if (Object.keys(inputs).length === 0 || definition.mode === 'collect') return false;
  try {
    const out = definition.cardinality(defaultConfig(definition), inputs);
    return Object.values(out).some((count) => count > 1);
  } catch {
    return false;
  }
}

const WORDS: Record<string, string> = {
  pov: 'POV',
  boldClaim: 'Bold claim',
  aiVoice: 'AI voiceover',
  watchTime: 'Watch time',
  brandTrack: 'Brand track',
  bestTime: 'Best time',
  tiktokWhite: 'TikTok white',
  tiktokBlack: 'TikTok black',
  wordByWord: 'Word by word',
  isNot: 'Is not',
  durationSeconds: 'Duration (s)',
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  LINKEDIN: 'LinkedIn',
  es: 'Spanish',
  de: 'German',
  fr: 'French',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  pl: 'Polish',
  ja: 'Japanese',
  ...Object.fromEntries(
    Object.entries(IMAGE_MODELS).map(([model, { label }]) => [model, label]),
  ),
  ...Object.fromEntries(
    Object.entries(VIDEO_MODELS).map(([model, { label }]) => [model, label]),
  ),
  // Scene video's quality, by its short side.
  standard: '720p',
  high: '1080p',
  '4k': '4K',
  asIs: 'As is',
};

/** A config key or enum value as words: "keepConstant" → "Keep constant". */
export function humanize(value: string): string {
  if (WORDS[value]) return WORDS[value];
  const words = value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
